"use client";

import { useState, useEffect } from "react";
import { MuxVideo } from "@/components/ui/mux-video";
import {
  VIDEO_ASSETS,
  getMuxThumbnail,
  type VideoAssetKey,
} from "@/lib/video-assets";

interface LevelRow {
  level: number;
  score: number;
  actions: number;
  base: number;
}

/** Helper to look up a Mux asset key by game ID */
function muxKey(gameId: string): VideoAssetKey | undefined {
  const key = `arc-agi-3/${gameId}` as VideoAssetKey;
  return key in VIDEO_ASSETS ? key : undefined;
}

interface GameTask {
  id: string;
  title: string;
  score: number;
  actions: number;
  gameId?: string;
  runId?: string;
  scorecardId?: string;
  levels?: LevelRow[];
}

const GAME_TASKS: GameTask[] = [
  {
    id: "cn04",
    title: "CN04",
    score: 97.6,
    actions: 118,
    gameId: "cn04",
    runId: "cn04-65d47d14_20260325_225706",
    scorecardId: "97.6% RHAE",
    levels: [
      { level: 1, score: 64.0, actions: 20, base: 16 },
      { level: 2, score: 100.0, actions: 19, base: 54 },
      { level: 3, score: 100.0, actions: 22, base: 69 },
      { level: 4, score: 100.0, actions: 21, base: 318 },
      { level: 5, score: 100.0, actions: 35, base: 208 },
    ],
  },
  {
    id: "lp85",
    title: "LP85",
    score: 84.16,
    actions: 273,
    gameId: "lp85",
    runId: "lp85-305b61c3_20260326_144152",
    scorecardId: "84.1564% RHAE",
    levels: [
      { level: 1, score: 100.0, actions: 17, base: 33 },
      { level: 2, score: 100.0, actions: 11, base: 22 },
      { level: 3, score: 100.0, actions: 18, base: 31 },
      { level: 4, score: 100.0, actions: 18, base: 23 },
      { level: 5, score: 100.0, actions: 23, base: 33 },
      { level: 6, score: 4.94, actions: 153, base: 34 },
      { level: 7, score: 100.0, actions: 19, base: 73 },
      { level: 8, score: 100.0, actions: 13, base: 173 },
    ],
  },
  {
    id: "ar25",
    title: "AR25",
    score: 83.28,
    actions: 516,
    gameId: "ar25",
    runId: "ar25-e3c63847_20260326_033618",
    scorecardId: "83.2784% RHAE",
    levels: [
      { level: 1, score: 11.56, actions: 50, base: 17 },
      { level: 2, score: 53.78, actions: 30, base: 22 },
      { level: 3, score: 100.0, actions: 97, base: 103 },
      { level: 4, score: 100.0, actions: 28, base: 29 },
      { level: 5, score: 15.79, actions: 73, base: 29 },
      { level: 6, score: 100.0, actions: 84, base: 159 },
      { level: 7, score: 100.0, actions: 106, base: 152 },
      { level: 8, score: 100.0, actions: 47, base: 66 },
    ],
  },
  {
    id: "ft09",
    title: "FT09",
    score: 77.59,
    actions: 123,
    gameId: "ft09",
    runId: "ft09-0d8bbf25_20260326_012458",
    scorecardId: "77.5875% RHAE",
    levels: [
      { level: 1, score: 100.0, actions: 3, base: 17 },
      { level: 2, score: 100.0, actions: 7, base: 19 },
      { level: 3, score: 100.0, actions: 14, base: 15 },
      { level: 4, score: 100.0, actions: 21, base: 21 },
      { level: 5, score: 100.0, actions: 21, base: 65 },
      { level: 6, score: 21.56, actions: 56, base: 26 },
    ],
  },
  {
    id: "cd82",
    title: "CD82",
    score: 70.15,
    actions: 204,
    gameId: "cd82",
    runId: "cd82-fb555c5d_20260326_101852",
    scorecardId: "70.1511% RHAE",
    levels: [
      { level: 1, score: 46.69, actions: 60, base: 41 },
      { level: 2, score: 4.94, actions: 36, base: 8 },
      { level: 3, score: 27.7, actions: 57, base: 30 },
      { level: 4, score: 100.0, actions: 14, base: 21 },
      { level: 5, score: 100.0, actions: 16, base: 19 },
      { level: 6, score: 72.25, actions: 20, base: 17 },
    ],
  },
  {
    id: "tu93",
    title: "TU93",
    score: 67.87,
    actions: 400,
    gameId: "tu93",
    runId: "tu93-2b534c15_20260326_012458",
    scorecardId: "67.8668% RHAE",
    levels: [
      { level: 1, score: 100.0, actions: 17, base: 19 },
      { level: 2, score: 69.44, actions: 18, base: 15 },
      { level: 3, score: 100.0, actions: 23, base: 34 },
      { level: 4, score: 87.11, actions: 45, base: 42 },
      { level: 5, score: 88.03, actions: 81, base: 76 },
      { level: 6, score: 100.0, actions: 62, base: 91 },
      { level: 7, score: 100.0, actions: 14, base: 47 },
      { level: 8, score: 6.39, actions: 91, base: 23 },
      { level: 9, score: 41.7, actions: 48, base: 31 },
    ],
  },
  {
    id: "sb26",
    title: "SB26",
    score: 49.35,
    actions: 581,
    gameId: "sb26",
    runId: "sb26-7fbdac44_20260326_012458",
    scorecardId: "49.3535% RHAE",
    levels: [
      { level: 1, score: 100.0, actions: 18, base: 18 },
      { level: 2, score: 0.52, actions: 221, base: 16 },
      { level: 3, score: 100.0, actions: 15, base: 15 },
      { level: 4, score: 56.25, actions: 20, base: 15 },
      { level: 5, score: 100.0, actions: 17, base: 31 },
      { level: 6, score: 100.0, actions: 19, base: 24 },
      { level: 7, score: 6.44, actions: 67, base: 17 },
      { level: 8, score: 0.7, actions: 203, base: 17 },
    ],
  },
];

const COLLAPSED_COUNT = 4;

function actionColor(actions: number, base: number) {
  if (actions <= base) return "#6ee7a0";
  return "rgba(255,255,255,0.75)";
}

function Scorecard({ task }: { task: GameTask }) {
  if (!task.levels) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "rgba(255,255,255,0.2)",
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: "12px",
        }}
      >
        No scorecard data
      </div>
    );
  }

  const totalActions = task.levels.reduce((s, r) => s + r.actions, 0);

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        padding: "0px 20px 16px",
        fontFamily: "'IBM Plex Mono', monospace",
        overflowY: "auto",
      }}
    >
      {/* Table */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "11px",
          marginTop: "56px",
        }}
      >
        <thead>
          {/* Game ID + overall score row */}
          <tr>
            <th
              style={{
                textAlign: "left",
                padding: "4px 8px 12px",
                fontSize: "15px",
                fontWeight: 600,
                color: "#fff",
              }}
            >
              {task.gameId ?? task.id}
            </th>
            <th colSpan={2} />
            <th
              style={{
                textAlign: "right",
                padding: "4px 8px 12px",
                fontSize: "18px",
                fontWeight: 500,
                color: "#9b9dff",
              }}
            >
              {task.score.toFixed(2)}%
            </th>
          </tr>
          {/* Column headers */}
          <tr>
            {["Level", "Score", "Actions", "Human"].map((h) => (
              <th
                key={h}
                style={{
                  textAlign: h === "Level" ? "left" : "right",
                  padding: "4px 8px",
                  color: "rgba(255,255,255,0.35)",
                  fontWeight: 400,
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                  paddingBottom: "8px",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {task.levels.map((row, i) => (
            <tr
              key={row.level}
              style={{
                background:
                  i % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent",
              }}
            >
              <td
                style={{ padding: "5px 8px", color: "rgba(255,255,255,0.75)" }}
              >
                {row.level}
              </td>
              <td
                style={{
                  padding: "5px 8px",
                  textAlign: "right",
                  color: "rgba(255,255,255,0.75)",
                }}
              >
                {row.score.toFixed(2)}
              </td>
              <td
                style={{
                  padding: "5px 8px",
                  textAlign: "right",
                  color: actionColor(row.actions, row.base),
                  fontWeight: 500,
                }}
              >
                {row.actions.toLocaleString()}
              </td>
              <td
                style={{
                  padding: "5px 8px",
                  textAlign: "right",
                  color: "rgba(255,255,255,0.35)",
                }}
              >
                {row.base}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2} />
            <td
              style={{
                padding: "6px 8px",
                textAlign: "right",
                borderTop: "1px solid rgba(255,255,255,0.08)",
                color: "rgba(255,255,255,0.5)",
                fontWeight: 500,
                fontSize: "11px",
              }}
            >
              {totalActions.toLocaleString()}
            </td>
            <td />
          </tr>
        </tfoot>
      </table>

      {/* Run ID */}
      {task.runId && (
        <div
          style={{
            marginTop: "auto",
            paddingTop: "14px",
            fontSize: "9px",
            color: "rgba(255,255,255,0.25)",
            lineHeight: 1.7,
            wordBreak: "break-all",
          }}
        >
          run: {task.runId}
          <br />
          scorecard: {task.scorecardId}
        </div>
      )}
    </div>
  );
}

function TaskModal({ task, onClose }: { task: GameTask; onClose: () => void }) {
  const assetKey = muxKey(task.gameId ?? task.id);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-3 sm:p-6"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="relative w-full flex flex-col sm:flex-row overflow-hidden rounded-2xl"
        style={{
          maxWidth: "960px",
          maxHeight: "92vh",
          background: "rgba(14,14,16,0.99)",
          border: "1px solid rgba(255,255,255,0.1)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.7)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Video panel */}
        <div
          className="flex flex-col sm:flex-[0_0_58%] min-h-0"
          style={{ background: "#000" }}
        >
          <div className="relative flex-1 min-h-0">
            {assetKey ? (
              <MuxVideo
                assetKey={assetKey}
                controls
                autoPlay={false}
                className="w-full h-full block"
                style={{ objectFit: "contain", maxHeight: "55vh" }}
              />
            ) : (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src="/images/blog/arcgentica/arc-agi-3-placeholder.png"
                alt=""
                className="w-full block"
                style={{ objectFit: "cover", maxHeight: "55vh" }}
              />
            )}
          </div>

          {/* Title bar */}
          <div
            className="flex items-center gap-2 px-3 py-2.5 sm:px-4"
            style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
          >
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#fff",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontFamily: "'IBM Plex Mono',monospace",
                fontSize: 12,
                color: "rgba(255,255,255,0.75)",
              }}
            >
              {task.title}
            </span>
            <span
              className="ml-auto"
              style={{
                fontFamily: "'IBM Plex Mono',monospace",
                fontSize: 11,
                color: "rgba(255,255,255,0.35)",
              }}
            >
              {task.actions.toLocaleString()} actions
            </span>
          </div>
        </div>

        {/* Scorecard panel */}
        <div
          className="overflow-y-auto sm:flex-[0_0_42%]"
          style={{
            borderTop: "1px solid rgba(255,255,255,0.07)",
            borderLeft: "none",
          }}
        >
          <Scorecard task={task} />
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 flex items-center justify-center"
          style={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: "rgba(0,0,0,0.7)",
            border: "1px solid rgba(255,255,255,0.2)",
            color: "rgba(255,255,255,0.8)",
            cursor: "pointer",
            fontSize: 16,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

function TaskCard({ task, onClick }: { task: GameTask; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);
  const assetKey = muxKey(task.gameId ?? task.id);
  const thumbnail = assetKey
    ? getMuxThumbnail(assetKey, { width: 912, height: 1150 })
    : "/images/blog/arcgentica/arc-agi-3-placeholder.png";

  return (
    <div
      style={{
        position: "relative",
        borderRadius: "0.625rem",
        overflow: "hidden",
        aspectRatio: "912/1150",
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.08)",
        cursor: "pointer",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      {/* Thumbnail / placeholder */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={thumbnail}
        alt=""
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
        }}
      />

      {/* Play icon — always visible */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
          opacity: hovered ? 0 : 1,
          transition: "opacity 0.2s ease",
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "rgba(0,0,0,0.55)",
            border: "1px solid rgba(255,255,255,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
            <polygon points="6 3 20 12 6 21 6 3" />
          </svg>
        </div>
      </div>

      {/* Hover overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.75)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          opacity: hovered ? 1 : 0,
          transition: "opacity 0.2s ease",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background: "#fff",
            boxShadow: `0 0 8px ${"#fff"}`,
          }}
        />
        <div
          style={{
            fontSize: "20px",
            fontWeight: 700,
            fontFamily: "'IBM Plex Mono', monospace",
            color: "#fff",
            lineHeight: 1,
          }}
        >
          {task.score}%
        </div>
        <div
          style={{
            marginTop: "4px",
            fontSize: "10px",
            fontFamily: "'IBM Plex Mono', monospace",
            color: "rgba(255,255,255,0.65)",
          }}
        >
          {task.actions.toLocaleString()} actions
        </div>
      </div>

      {/* Bottom gradient bar — always visible */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "40px 10px 10px",
          background:
            "linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          maskImage: "linear-gradient(to top, black 40%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to top, black 40%, transparent 100%)",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          pointerEvents: "none",
        }}
      >
        <span
          style={{
            fontSize: "10px",
            fontFamily: "'IBM Plex Mono', monospace",
            color: "rgba(255,255,255,0.75)",
            letterSpacing: "0.04em",
          }}
        >
          {task.title}
        </span>
        <span
          style={{
            fontSize: "10px",
            fontFamily: "'IBM Plex Mono', monospace",
            color: "#6ee7a0",
            letterSpacing: "0.04em",
            fontWeight: 600,
          }}
        >
          {task.score}% WIN
        </span>
      </div>

      {/* Score dot */}
      <div
        style={{
          position: "absolute",
          top: "8px",
          right: "8px",
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          background: "#fff",
          opacity: hovered ? 0 : 0.9,
          transition: "opacity 0.2s ease",
        }}
      />
    </div>
  );
}

export function GameBreakdownGrid() {
  const [expanded, setExpanded] = useState(false);
  const [activeTask, setActiveTask] = useState<GameTask | null>(null);
  const visibleTasks = expanded
    ? GAME_TASKS
    : GAME_TASKS.slice(0, COLLAPSED_COUNT);

  return (
    <>
      <div style={{ margin: "24px 0" }}>
        <div className="grid grid-cols-2 gap-4 mb-7">
          {visibleTasks.map((task) => (
            <div key={task.id}>
              <TaskCard task={task} onClick={() => setActiveTask(task)} />
            </div>
          ))}
        </div>

        <div className="flex justify-center mt-8">
          <button
            onClick={() => setExpanded(!expanded)}
            className="inline-flex items-center justify-center text-sm font-mono font-medium uppercase rounded-full px-6 py-3 transition-colors duration-200 cursor-pointer bg-transparent border border-white/30 hover:border-white/60 text-white"
          >
            {expanded ? "Show less ↑" : `Show all ${GAME_TASKS.length} games ↓`}
          </button>
        </div>
      </div>

      {activeTask && (
        <TaskModal task={activeTask} onClose={() => setActiveTask(null)} />
      )}
    </>
  );
}
